import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import axios from 'axios';
import { useAiDraft, useCreateTemplateFromDraft } from './hooks';
import { useDocumentKinds } from './kindHooks';
import { defaultsEffectiveBound, humanizeDocumentKind } from './kindLabels';
import type { AiTemplateDraft, TemplateKind } from './types';

const MAX_DESCRIPTION = 2000;

// Local editable shape: bullets are edited as one-per-line text.
interface EditSection {
  heading: string;
  paragraphs: string[];
  bulletsText: string;
}

interface EditDraft {
  title: string;
  sections: EditSection[];
}

function toEdit(draft: AiTemplateDraft): EditDraft {
  return {
    title: draft.title,
    sections: draft.sections.map((s) => ({
      heading: s.heading ?? '',
      paragraphs: [...s.paragraphs],
      bulletsText: (s.bullets ?? []).join('\n')
    }))
  };
}

function toDraft(edit: EditDraft): AiTemplateDraft {
  return {
    title: edit.title.trim(),
    sections: edit.sections.map((s) => {
      const bullets = s.bulletsText
        .split('\n')
        .map((b) => b.trim())
        .filter((b) => b.length > 0);
      return {
        heading: s.heading.trim().length > 0 ? s.heading.trim() : null,
        paragraphs: s.paragraphs.map((p) => p.trim()).filter((p) => p.length > 0),
        bullets: bullets.length > 0 ? bullets : null
      };
    })
  };
}

// Render prose with {{Token}} spans highlighted as badges.
function renderWithTokens(text: string) {
  return text.split(/(\{\{[^}]*\}\})/g).map((part, i) =>
    /^\{\{[^}]*\}\}$/.test(part) ? (
      <span
        key={i}
        className="inline-block bg-sky-100 text-sky-700 rounded px-1 font-mono text-xs align-baseline"
      >
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function apiError(err: unknown): { message: string; status?: number; invalidFieldNames?: string[] } {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; invalidFieldNames?: string[] } | undefined;
    return {
      message: data?.message ?? 'Something went wrong.',
      status: err.response?.status,
      invalidFieldNames: data?.invalidFieldNames
    };
  }
  return { message: 'Something went wrong.' };
}

export function AiTemplateComposer() {
  const { data: kinds } = useDocumentKinds();
  const aiDraft = useAiDraft();
  const createFromDraft = useCreateTemplateFromDraft();

  const [unavailable, setUnavailable] = useState(false);
  const [description, setDescription] = useState('');
  const [edit, setEdit] = useState<EditDraft | null>(null);

  // Save-stage fields.
  const [name, setName] = useState('');
  const [kind, setKind] = useState<TemplateKind>('');
  const [isEffectiveDateBound, setIsEffectiveDateBound] = useState(false);
  const [requiresSignature, setRequiresSignature] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[] | null>(null);

  const effectiveKind = kind || kinds?.[0]?.name || '';

  if (unavailable) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Sparkles size={18} className="text-slate-400" /> Create with AI
        </h2>
        <p className="text-sm text-slate-500 mt-2">AI template creation is not available.</p>
      </section>
    );
  }

  const onGenerate = async () => {
    if (!description.trim()) return;
    setError(null);
    setInvalidFields(null);
    try {
      const draft = await aiDraft.mutateAsync(description.trim());
      setEdit(toEdit(draft));
      setName(draft.title);
      setKind('');
      setIsEffectiveDateBound(false);
      setRequiresSignature(true);
    } catch (err) {
      const e = apiError(err);
      if (e.status === 503) {
        setUnavailable(true);
        return;
      }
      setError(e.message);
    }
  };

  const onSave = async () => {
    if (!edit || !name.trim() || !effectiveKind) return;
    setError(null);
    setInvalidFields(null);
    try {
      await createFromDraft.mutateAsync({
        draft: toDraft(edit),
        name: name.trim(),
        kind: effectiveKind,
        isEffectiveDateBound,
        requiresSignature
      });
      // Reset the composer; the new template appears in the list below.
      setEdit(null);
      setDescription('');
      setName('');
      setKind('');
    } catch (err) {
      const e = apiError(err);
      setError(e.message);
      setInvalidFields(e.invalidFieldNames ?? null);
    }
  };

  const updateSection = (i: number, patch: Partial<EditSection>) =>
    setEdit((d) =>
      d ? { ...d, sections: d.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) } : d
    );

  const updateParagraph = (si: number, pi: number, value: string) =>
    setEdit((d) =>
      d
        ? {
            ...d,
            sections: d.sections.map((s, idx) =>
              idx === si ? { ...s, paragraphs: s.paragraphs.map((p, j) => (j === pi ? value : p)) } : s
            )
          }
        : d
    );

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Sparkles size={18} className="text-sky-500" /> Create with AI
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Describe the document you need. The AI drafts it using your merge fields; you review and
          edit before it becomes a template.
        </p>
      </div>

      {!edit ? (
        // ── Describe ──
        <div className="space-y-3">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Describe the document</span>
            <textarea
              value={description}
              maxLength={MAX_DESCRIPTION}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="e.g. A pet-sitting service agreement covering key handling, emergency vet authorization, and liability."
              className="w-full border border-slate-300 rounded px-3 py-2"
            />
            <span className="block text-xs text-slate-400 mt-1 text-right">
              {description.length}/{MAX_DESCRIPTION}
            </span>
          </label>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>
          )}

          <button
            type="button"
            onClick={onGenerate}
            disabled={!description.trim() || aiDraft.isPending}
            className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
          >
            {aiDraft.isPending ? 'Generating…' : 'Generate draft'}
          </button>
        </div>
      ) : (
        // ── Preview / edit + Save ──
        <div className="space-y-5">
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Editors */}
            <div className="space-y-4">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 mb-1">Title</span>
                <input
                  type="text"
                  value={edit.title}
                  onChange={(e) => setEdit((d) => (d ? { ...d, title: e.target.value } : d))}
                  className="w-full border border-slate-300 rounded px-3 py-2"
                />
              </label>

              {edit.sections.map((s, si) => (
                <div key={si} className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <label className="block">
                    <span className="block text-xs font-medium text-slate-500 mb-1">Heading</span>
                    <input
                      type="text"
                      value={s.heading}
                      onChange={(e) => updateSection(si, { heading: e.target.value })}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                  {s.paragraphs.map((p, pi) => (
                    <textarea
                      key={pi}
                      value={p}
                      onChange={(e) => updateParagraph(si, pi, e.target.value)}
                      rows={3}
                      aria-label={`Section ${si + 1} paragraph ${pi + 1}`}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  ))}
                  <label className="block">
                    <span className="block text-xs font-medium text-slate-500 mb-1">Bullets (one per line)</span>
                    <textarea
                      value={s.bulletsText}
                      onChange={(e) => updateSection(si, { bulletsText: e.target.value })}
                      rows={3}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                </div>
              ))}
            </div>

            {/* Read-only rendered preview with token badges */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3 text-sm text-slate-700">
              <h3 className="text-base font-semibold text-slate-800">{renderWithTokens(edit.title)}</h3>
              {edit.sections.map((s, si) => (
                <div key={si} className="space-y-1">
                  {s.heading.trim() && (
                    <h4 className="font-semibold text-slate-700">{renderWithTokens(s.heading)}</h4>
                  )}
                  {s.paragraphs
                    .filter((p) => p.trim())
                    .map((p, pi) => (
                      <p key={pi}>{renderWithTokens(p)}</p>
                    ))}
                  {s.bulletsText.trim() && (
                    <ul className="list-disc list-inside">
                      {s.bulletsText
                        .split('\n')
                        .map((b) => b.trim())
                        .filter((b) => b)
                        .map((b, bi) => (
                          <li key={bi}>{renderWithTokens(b)}</li>
                        ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                // Regenerate: back to Describe, keep the description.
                setEdit(null);
                setError(null);
                setInvalidFields(null);
              }}
              className="text-sm px-3 py-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              Regenerate
            </button>
            <button
              type="button"
              onClick={() => {
                // Discard: reset everything.
                setEdit(null);
                setDescription('');
                setError(null);
                setInvalidFields(null);
              }}
              className="text-sm px-3 py-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              Discard
            </button>
          </div>

          {/* Save */}
          <div className="border-t border-slate-100 pt-4 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 mb-1">Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 mb-1">Kind</span>
                <select
                  value={effectiveKind}
                  onChange={(e) => {
                    const k = e.target.value;
                    setKind(k);
                    if (defaultsEffectiveBound(k)) setIsEffectiveDateBound(true);
                  }}
                  className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
                >
                  {(kinds ?? []).map((k) => (
                    <option key={k.id} value={k.name}>
                      {humanizeDocumentKind(k.name)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isEffectiveDateBound}
                onChange={(e) => setIsEffectiveDateBound(e.target.checked)}
                className="mt-1"
              />
              <span>
                Expire documents after visit. Effective window runs from visit start to visit end +
                grace period.
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={requiresSignature}
                onChange={(e) => setRequiresSignature(e.target.checked)}
                className="mt-1"
              />
              <span>
                Requires signature — the customer must sign this document. Uncheck to send it as a
                confirmation copy with no signing link.
              </span>
            </label>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                <p>{error}</p>
                {invalidFields && invalidFields.length > 0 && (
                  <p className="mt-1 text-xs">
                    Invalid field names: <code>{invalidFields.join(', ')}</code>
                  </p>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={onSave}
              disabled={!name.trim() || createFromDraft.isPending}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
            >
              {createFromDraft.isPending ? 'Saving…' : 'Save as template'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
